import type { Ref, ReactNode } from 'react';

import type { TimeTablingItemColor } from '../TimeTabling/types';
import { ScheduleGridDayColumnProps } from './ScheduleGridDayColumn';
import { ScheduleGridNowIndicatorProps } from './ScheduleGridNowIndicator';
import { ScheduleGridTimeColumnProps } from './ScheduleGridTimeColumn';

export enum ScheduleItemSize {
  Minimal = 'minimal',
  Compact = 'compact',
  Full = 'full',
}

export type EndAdornmentProps = { size: ScheduleItemSize };

export type ScheduleGridItemData<T = unknown> = {
  id: string;
  day: string;
  startTime: string;
  endTime: string;
  color: TimeTablingItemColor;
  /** Optional data-test-id for the rendered slot (e.g. for E2E targeting). */
  dataTestId?: string;
  data: T;
  label: string | string[];
  onLabelClick?: () => void;
  endAdornment?: (props: EndAdornmentProps) => ReactNode;
  /**
   * When set, a two-tone overlay triangle is painted on the item using this
   * colour (e.g. a "Lesson + Registration" slot overlays the Registration
   * colour on the Lesson background).
   */
  twoToneColor?: TimeTablingItemColor;
};

export type GridColumn = {
  label: string;
  value: string;
};

export type ItemComponentProps<T> = {
  item: ScheduleGridItemData<T>;
  size: ScheduleItemSize;
  height: number;
};

export type TooltipComponentProps<T> = {
  item: ScheduleGridItemData<T>;
  size: ScheduleItemSize;
  children: ReactNode;
};

export type SizeThresholds = {
  minimal: number;
  compact: number;
};

export type ScheduleGridProps<T> = {
  items: ScheduleGridItemData<T>[];
  days: GridColumn[];
  timeRange: { start: string; end: string };
  hourHeight?: number;
  gridLinesPerHour?: number;
  showTimeColumn?: boolean;
  sizeThresholds?: SizeThresholds;
  ItemComponent?: React.ComponentType<ItemComponentProps<T>>;
  TooltipComponent?: React.ComponentType<TooltipComponentProps<T>>;
};

export type ScheduleDayGridProps<T> = Omit<ScheduleGridProps<T>, 'days'> & {
  showNowIndicator?: boolean;
  nowIndicatorProps?: ScheduleGridNowIndicatorProps & { ref?: Ref<HTMLDivElement> };
  timeColumnProps?: ScheduleGridTimeColumnProps;
  dayColumnProps?: ScheduleGridDayColumnProps;
};

export type ScheduleGridContextValue = {
  hourHeight: number;
  gridLinesPerHour: number;
  timeRange: { start: string; end: string };
  getItemHeight: (startTime: string, endTime: string) => number;
  getItemTop: (startTime: string) => number;
  getItemSize: (height: number) => ScheduleItemSize;
};
